import React, {
    memo,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import { useDispatch } from 'react-redux'

import { uiId } from 'services/utils'

import { getPlaybooks } from 'services/swMessenger'
import { setTags } from 'ducks/actions'

import { Box, Button, Typography } from '@mui/material'

import makeStyles from '@mui/styles/makeStyles'

import IconFlagRed from 'assets/icons/icon-flag-reg.svg'

import { PlaybookHits } from 'UI/Pages/playbooks/PlaybookHits'
import {
    SearchByAppContext,
    isMainSpace,
    generateSpaceIds,
} from './PagePlaybooks'

import { useBoolean, TypeUseBoolean, usePrevious } from 'hooks'
import { isEqual } from '../../../services/lodash'
import { AppContext } from '../../../app/AppProvider'

const PAGE_SIZE = 8

const useStyles = makeStyles({
    noResult: {
        textAlign: 'center',
    },
    emptySubTitle: {
        color: '#78909c',
    },
})

type Props = {
    index: number
    tags: Array<string>
    defaultTags?: Array<string> | null
    applicationId: string
    hasMore?: TypeUseBoolean
    increasePage: () => void
    spaces: string[]
}

const Page = memo(
    ({
        index,
        tags,
        defaultTags,
        applicationId,
        hasMore,
        increasePage,
        spaces,
    }: Props) => {
        const { setSearchResult, setSearchWithDefaultTagsResult } =
            useContext(SearchByAppContext)
        const { isLibraryOpened } = useContext(AppContext)
        const dispatch = useDispatch()
        const classes = useStyles()

        const [data, setData] = useState<any>(undefined)
        const [prevReqParams, setPrevReqParams] = useState<any>(undefined)

        useEffect(() => {
            if (!data) return

            setSearchResult(data?.playbooks?.length < 1)
        }, [data, setSearchResult])

        const requestPlaybooks = useCallback(
            (revalidateData: boolean) => {
                // prevent api call before we get default tags for current page
                if (defaultTags === null) return

                const requestParams = {
                    size: PAGE_SIZE,
                    sortBy: 'date',
                    page: index,
                    tags: [
                        ...(defaultTags
                            ? defaultTags.filter(
                                  defTag => !tags.includes(defTag)
                              )
                            : []),
                        ...tags,
                    ],
                    apps: applicationId,
                    isMainSpace: isMainSpace(spaces),
                    spaceIds: generateSpaceIds(spaces),
                }

                // Sometimes request params are changing fast (e.g. when you open library while it's still loading).
                // To make sure we don't trigger duplicate requests, check if the params had actually changed.
                // ONLY FOR THE FIRST PAGE!
                if (index === 0) {
                    if (
                        !revalidateData &&
                        isEqual(requestParams, prevReqParams)
                    ) {
                        // Got duplicate request, ignore it, params didn't actually change
                        return
                    } else {
                        setPrevReqParams(requestParams)
                    }
                }

                getPlaybooks(payload => {
                    if (index === 0) {
                        const emptyWithDefaultTags =
                            defaultTags &&
                            defaultTags.length > 0 &&
                            payload?.playbooks?.length === 0

                        setSearchWithDefaultTagsResult(!!emptyWithDefaultTags)

                        if (emptyWithDefaultTags) {
                            const fallbackParams = {
                                size: PAGE_SIZE,
                                sortBy: 'date',
                                page: index,
                                search: defaultTags?.join(' '),
                                tags: tags.filter(
                                    tag => !defaultTags?.includes(tag)
                                ),
                                apps: applicationId,
                                isMainSpace: isMainSpace(spaces),
                                spaceIds: generateSpaceIds(spaces),
                            }

                            return getPlaybooks(newPayload => {
                                dispatch(
                                    setTags(newPayload?.facets?.tags || null)
                                )
                                setData(newPayload)
                            }, fallbackParams)
                        }
                        dispatch(setTags(payload?.facets?.tags || null))
                    }

                    setData(payload)
                }, requestParams)
            },
            // eslint-disable-next-line
            [
                dispatch,
                index,
                applicationId,
                tags,
                defaultTags,
                spaces,
                prevReqParams,
            ]
        )

        const prevLibState = usePrevious(isLibraryOpened)

        useEffect(() => {
            if (isLibraryOpened) {
                // On library open we ALWAYS revalidate data
                requestPlaybooks(prevLibState !== isLibraryOpened)
            }
        }, [isLibraryOpened, prevLibState, requestPlaybooks])

        const playbooks = data?.playbooks
        const hitsNumber = data?.nbHits

        useEffect(() => {
            if (hasMore !== undefined) {
                if (hitsNumber && hitsNumber > (index + 1) * PAGE_SIZE) {
                    hasMore.setTrue()
                } else {
                    hasMore.setFalse()
                }
            }
        }, [hasMore, hitsNumber, index])

        if (data === null || playbooks?.length === 0) {
            return (
                <Box mt={4} className={classes.noResult}>
                    <img src={IconFlagRed} alt="red flag" />
                    <Typography>
                        <b>No results found </b>
                    </Typography>
                    {EXT_MODE && (
                        <Typography
                            variant="body2"
                            className={classes.emptySubTitle}
                        >
                            <b>
                                Couldn't find what you were looking for? You can
                                always ask a Guidde
                            </b>
                        </Typography>
                    )}
                </Box>
            )
        }

        if (playbooks?.length) {
            return (
                <>
                    <PlaybookHits playbooks={playbooks} />
                    {hasMore?.isTrue && (
                        <Box mt={3} display="inline-block">
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={event => {
                                    event.preventDefault()
                                    increasePage()
                                }}
                                id={uiId('show-more-btn')}
                            >
                                <Typography component="span">
                                    Show more
                                </Typography>
                            </Button>
                        </Box>
                    )}
                </>
            )
        }

        return (
            <Box mt={3} mb={5} pb={3}>
                <Typography>Loading...</Typography>
            </Box>
        )
    }
)

export const PaginatedPlaybooks = memo(
    ({ appFilter, tagsFilter, spaceFilter }: any) => {
        const [page, setPage] = useState(0)
        const increasePage = () => setPage(prevState => prevState + 1)
        const resetPage = () => setPage(0)

        const hasMore = useBoolean()

        const [pages, setPages] = useState<Array<JSX.Element>>([])

        // reset current page on each app and tags change
        useEffect(() => {
            resetPage()
            setPages([])
        }, [appFilter.state, tagsFilter.state])

        const rerenderPages = () => {
            const tempPages: Array<JSX.Element> = []

            for (let i = 0; i <= page; i++) {
                tempPages.push(
                    <Page
                        index={i}
                        key={i}
                        hasMore={i === page ? hasMore : undefined}
                        increasePage={increasePage}
                        applicationId={
                            SDK_MODE
                                ? ''
                                : appFilter.state ||
                                  appFilter.defaultState ||
                                  ''
                        }
                        tags={tagsFilter.state}
                        defaultTags={
                            i === 0 ? tagsFilter.defaultState : undefined
                        }
                        spaces={spaceFilter.state}
                    />
                )
            }
            setPages(tempPages)
        }

        useEffect(() => {
            rerenderPages()
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [page, hasMore, spaceFilter.state])

        useEffect(() => {
            if (page !== 0) {
                return undefined
            }
            rerenderPages()
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            appFilter.state,
            appFilter.defaultState,
            tagsFilter.defaultState,
            tagsFilter.state,
        ])

        return <Box id={uiId('playbooks-list')}>{pages}</Box>
    }
)
